// Sharing a catch or a pin to one of your groups.
import type { Catch, Group, Payload, Pin } from "../../shared/model.ts";
import { dbGet } from "../db.ts";
import { el, toast } from "../dom.ts";
import { t } from "../i18n.ts";
import { blobToDataUrl, shrink } from "../photos.ts";
import { cancelButton, closeSheet, grow, openSheet, primaryButton } from "../sheet.ts";
import { speciesName } from "../species.ts";
import { errorMessage } from "../util.ts";
import { hasWater } from "../water.ts";
import { groups, send } from "./store.ts";
import { renderGroups } from "./ui.ts";

/** Pick a group, then send what `makePayload` builds. */
function shareSheet(title: string, extra: HTMLElement[], makePayload: () => Promise<Payload>): void {
  if (!groups.length) { toast(t("shareNoGroups")); return; }
  let chosen: Group | null = null;
  const items = groups.map((g) => el("button", { class: "chip", type: "button", "aria-pressed": "false", onclick: (e) => {
    chosen = g;
    for (const c of items) c.setAttribute("aria-pressed", String(c === e.currentTarget));
  } }, g.name));
  openSheet(title, [el("div", { class: "f" }, el("span", { class: "lbl", text: t("shareTo") }), el("div", { class: "chips" }, ...items)), ...extra],
    [grow(), cancelButton(), primaryButton(t("shareSend"), async (btn) => {
      const g = chosen;
      if (!g) { toast(t("shareChoose")); return; }
      btn.disabled = true;
      try { await send(g, await makePayload()); closeSheet(); renderGroups(); toast(t("shareSent", g.name)); }
      catch (err) { console.error(err); toast(errorMessage(err).includes("too big") ? t("msgTooBig") : t("msgNotSent")); btn.disabled = false; }
    })]);
  if (groups.length === 1) items[0].click();
}

export function shareCatch(c: Catch): void {
  const spot = el("input", { type: "checkbox", id: "share-spot" });
  shareSheet(t("shareCatchTitle"), [
    el("label", { class: "toggle" }, spot, el("span", { text: t("shareSpot") })),
    el("p", { class: "hint", text: t("shareSpotHint") }),
  ], async () => {
    const w = c.weather?.status === "ok" ? c.weather : null;
    let image: string | null = null;
    const first = c.photoIds?.[0];
    if (first) { const p = await dbGet("photos", first); if (p?.full) image = await blobToDataUrl(await shrink(p.full, 1280, 0.78)); }
    return { type: "catch", image, catch: {
      species: speciesName(c), kind: c.kind, weightKg: c.weightKg, lengthCm: c.lengthCm, released: !!c.released, time: c.time,
      // the place name and spot only when the sender ticks "share the spot"
      place: spot.checked ? c.place : "", loc: spot.checked && c.loc ? { lat: c.loc.lat, lon: c.loc.lon } : null,
      temp: w?.temp, wind: w?.wind, windDir: w?.windDir, water: hasWater(c) ? c.water.value : null,
    } };
  });
}

export function sharePin(p: Pin): void {
  shareSheet(t("sharePinTitle"), [el("p", { class: "hint", text: t("sharePinHint") })],
    async () => ({ type: "pin", pin: { type: p.type, name: p.name, notes: p.notes, lat: p.lat, lon: p.lon } }));
}
